import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { FormattedNumber } from 'react-intl';

import { CMSHeading } from '@swf/cms';
import CloseButton, { Variant } from '@swf/components/StarsModal/components/CloseButton';
import { fetchJson } from '@swf/core/utils/api';

// TODO refactor to use composition or shared.scss
// eslint-disable-next-line no-restricted-imports
import styles from '../../Events.scss';

export interface EventInfo {
  title: string;
  onClose: () => void;
  startDate: Date;
  startingStack: string;
  buyin: string;
  sopUrl: string;
  sopDescription: string;
  entry: { cost: string; fee: string; house: string };
  reEntry: { cost: string; fee: string; house: string };
  bounty: { cost: string; house: string };
}

export interface EventDetails {
  level: string;
  duration: number;
  smallBlind: number;
  bigBlind: number;
  ante: number;
  title: string;
}

interface EventData {
  list: [
    {
      // eslint-disable-next-line id-blacklist
      number: string;
      duration: number;
      sb: string;
      bb: string;
      ante: string;
      title: string;
    }
  ];
}

const extractEventDetails = (data: EventData) =>
  data?.list?.map((item) => ({
    level: item.number,
    duration: item.duration,
    smallBlind: parseInt(item.sb),
    bigBlind: parseInt(item.bb),
    ante: parseInt(item.ante),
    title: item.title
  })) as unknown as EventDetails[];

const calculateTotalBuyIn = (entry: EventInfo['entry'], bounty: EventInfo['bounty']) => {
  let value = 0;

  if (entry?.cost) {
    value += parseInt(entry.cost.replace(/\D/g, ''));
  }

  if (entry?.fee) {
    value += parseInt(entry.fee.replace(/\D/g, ''));
  }

  if (bounty?.cost) {
    value += parseInt(bounty.cost.replace(/\D/g, ''));
  }

  return value;
};

const calculatePrizepool = (entry: EventInfo['entry'], bounty: EventInfo['bounty']) => {
  let value = 0;

  if (entry?.cost) {
    value += parseInt(entry.cost.replace(/\D/g, ''));
  }

  if (entry?.house) {
    value -= parseInt(entry.house.replace(/\D/g, ''));
  }

  if (bounty?.house) {
    value -= parseInt(bounty.house.replace(/\D/g, ''));
  }

  return value;
};

const calculateStaffCharge = (entry: EventInfo['entry'], bounty: EventInfo['bounty']) => {
  let value = 0;

  if (entry?.house) {
    value += parseInt(entry.house.replace(/\D/g, ''));
  }

  if (bounty?.house) {
    value += parseInt(bounty.house.replace(/\D/g, ''));
  }

  return value;
};

const getCurrencySymbol = (entry: EventInfo['entry']) => {
  const [currency] = entry?.cost.replace(/[0-9]/g, '') || '';

  return currency;
};

const EventModal: React.FC<EventInfo> = ({
  title,
  buyin,
  onClose,
  startDate,
  startingStack,
  entry,
  bounty,
  sopUrl,
  sopDescription
}) => {
  const [eventDetails, setEventDetails] = useState<EventDetails[]>([]);

  useEffect(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    const fetchData = async () => {
      try {
        const data = await fetchJson<EventData>(sopUrl, { signal });
        setEventDetails(extractEventDetails(data));
      } catch {
        setEventDetails([]);
      }
    };

    fetchData();

    return () => abortController.abort();
  }, [sopUrl]);

  return (
    <div className={styles.eventInfo}>
      <CMSHeading title={`${buyin} - ${title}`} level="h3" size="l" hero={false} />
      <CloseButton variant={Variant.Dark} onClick={onClose} />
      <div className={styles.date}>
        <strong>Date: {format(startDate, 'MMMM dd EEEE')}</strong> | <strong>Start Time:</strong>{' '}
        {format(startDate, 'HH:mm')} | <strong>Starting stack</strong> -{' '}
        {startingStack ? startingStack : 'Continued from previous event'}
      </div>
      <div className={styles.eventDetailsBreakdown}>
        <div>
          <strong>Buy-In Breakdown:</strong>
        </div>
        <ul className={styles.breakdownList}>
          <li>
            <strong>Total Buy in</strong> -{getCurrencySymbol(entry)}
            <FormattedNumber style="decimal" value={calculateTotalBuyIn(entry, bounty)} />
          </li>
          <li>
            <strong>Tournament Prizepool</strong> –{getCurrencySymbol(entry)}
            {<FormattedNumber style="decimal" value={calculatePrizepool(entry, bounty)} />}
          </li>
          <li>
            <strong>Bounty</strong> – {bounty?.cost}
          </li>
          <li>
            <strong>House fee</strong> – {entry?.fee}
          </li>
          <li>
            <strong>Staff Charge</strong> –{getCurrencySymbol(entry)}
            <FormattedNumber style="decimal" value={calculateStaffCharge(entry, bounty)} />
          </li>
        </ul>
      </div>
      <ul className={styles.eventDetailsList}>
        {sopDescription.split('•').map((item, i) => item && <li key={i}>{item}</li>)}
      </ul>
      <div className={styles.tableContainer}>
        <table className={styles.eventsTable}>
          <thead>
            <tr>
              <th>Level</th>
              <th>Duration</th>
              <th>Small Blind</th>
              <th>Big Blind</th>
              <th>Ante</th>
            </tr>
          </thead>
          <tbody>
            {eventDetails?.map((item, index) => (
              <tr key={index}>
                {!!item.level && (
                  <React.Fragment>
                    <td>{item.level}</td>
                    <td>{`${item.duration} min`}</td>
                    <td>
                      <FormattedNumber style="decimal" value={item.smallBlind} />
                    </td>
                    <td>
                      <FormattedNumber style="decimal" value={item.bigBlind} />
                    </td>
                    <td>
                      <FormattedNumber style="decimal" value={item.ante} />
                    </td>
                  </React.Fragment>
                )}
                {!item.level && (
                  <td className={styles.eventDetailsDay} colSpan={5}>
                    <strong>{item.title}</strong>
                    <strong className={styles.separator}>{`${item.duration} min`}</strong>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EventModal;
